import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

import api from '../services/apiService';
import { onboardingLogger } from '../utils/loggerInstance';
import { onboardingService } from '../services/onboardingService';
import { useOptionalWorkspace } from './WorkspaceContext';
import { WORKSPACE_DESCRIPTION_MAX_LENGTH } from '../schemas/onboarding';

import { useAuth } from './AuthContext';

interface OnboardingContextType {
  userId: string | null;
  userEmail: string | null;
  selectedGoals: string[];
  setSelectedGoals: (goals: string[]) => void;
  selectedIndustries: string[];
  setSelectedIndustries: (industries: string[]) => void;
  workspaceName: string;
  setWorkspaceName: (name: string) => void;
  workspaceDescription: string;
  setWorkspaceDescription: (description: string) => void;
  createdWorkspaceId: string | null;
  saveOnboardingData: () => Promise<{ success: boolean; workspaceId?: string }>;
  isLoading: boolean;
  error: string | null;
}

interface OnboardingProviderProps {
  children: ReactNode;
  initialSelectedIndustries?: string[];
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
  initialSelectedIndustries = [],
}) => {
  const { user } = useAuth();
  // Optional: nested OnboardingProvider remounts during Vite HMR can briefly lose the
  // WorkspaceProvider instance identity even when still nested under one.
  const workspace = useOptionalWorkspace();
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(initialSelectedIndustries);
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [workspaceDescription, setWorkspaceDescription] = useState<string>('');
  const [createdWorkspaceId, setCreatedWorkspaceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update selected industries when initialSelectedIndustries changes
  useEffect(() => {
    if (initialSelectedIndustries.length > 0) {
      setSelectedIndustries(initialSelectedIndustries);
    }
  }, [initialSelectedIndustries]);

  const saveOnboardingData = async (): Promise<{ success: boolean; workspaceId?: string }> => {
    if (!user?.id || !user?.email) {
      const errorMsg = 'User information is missing. Please sign in again.';
      setError(errorMsg);
      onboardingLogger.error(errorMsg, {
        userId: user?.id,
        userEmail: user?.email,
        action: 'saveOnboardingData',
      } as const);
      return { success: false };
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        user_id: user.id,
        email: user.email,
        goals: selectedGoals,
        industries: selectedIndustries,
        workspace_name: workspaceName,
        workspace_description:
          workspaceDescription.trim().slice(0, WORKSPACE_DESCRIPTION_MAX_LENGTH) || undefined,
      };

      onboardingLogger.debug('Attempting to save onboarding data', {
        payload,
        action: 'saveOnboardingData',
      } as const);

      // Use our API service to make the request
      const response = await api.post('/api/v1/users/onboarding', payload);

      onboardingLogger.debug('Successfully saved onboarding data', {
        response: response.data,
        action: 'saveOnboardingData',
      } as const);

      // Extract workspace ID from response if available
      const workspaceId = response.data?.workspace?.id;
      if (workspaceId) {
        setCreatedWorkspaceId(workspaceId);
        onboardingLogger.debug('Captured created workspace ID', {
          workspaceId,
          action: 'saveOnboardingData',
        });
      }

      // Keep WorkspaceContext in sync so the new workspace appears immediately in selectors.
      // Skip when workspace context is temporarily unavailable (e.g. Vite HMR remount).
      if (workspace) {
        const updatedWorkspaces = await workspace.refreshWorkspaces();
        if (workspaceId) {
          const createdWorkspace = updatedWorkspaces.find(
            (candidate) => candidate.id === workspaceId,
          );
          if (createdWorkspace) {
            workspace.setSelectedWorkspace(createdWorkspace);
          }
        }
      } else {
        onboardingLogger.warn(
          'Workspace context unavailable after onboarding save; skipping workspace refresh',
          { workspaceId, action: 'saveOnboardingData' },
        );
      }

      // Mark onboarding as completed in cache to avoid repeated API calls
      onboardingService.markOnboardingCompleted(user.id);

      return { success: true, workspaceId };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      onboardingLogger.error('Error saving onboarding data', {
        error: err,
        errorMessage,
        action: 'saveOnboardingData',
      } as const);
      setError(errorMessage);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    userId: user?.id || null,
    userEmail: user?.email || null,
    selectedGoals,
    setSelectedGoals,
    selectedIndustries,
    setSelectedIndustries,
    workspaceName,
    setWorkspaceName,
    workspaceDescription,
    setWorkspaceDescription,
    createdWorkspaceId,
    saveOnboardingData,
    isLoading,
    error,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
